import type { FlowMetrics, KpiBucket, OverallTotals } from '../../../client/IvtHubClient';
import { ABSENT_MARK, formatArr } from './scale';
import { VitalityPills } from './VitalityPills';

/**
 * The headline band: six flow buckets plus the asset vitality group.
 *
 * Reads `total` only — the totals across the industries in scope for this caller,
 * which is NOT a company-wide figure. Five of the six cells are scoped to the
 * requested window; 30 天未更新 is not, and the note on that cell says so.
 *
 * The order is the design's and differs from the industry table's column order.
 * That is intentional: the band leads with momentum because it is the number a
 * leader is asked about, while the table leads with total open because that is
 * what rows get compared on.
 */

interface Cell {
  label: string;
  bucket: (m: FlowMetrics | undefined) => KpiBucket | undefined;
  /**
   * Colour class; the three neutral cells share the default. Three levels, not two:
   * 30 天未更新 is amber and 丢单 is red, because they are not equally bad — an untouched
   * open deal can still be rescued, a lost one cannot. Spending the strongest colour on
   * the recoverable one would leave nothing louder for the irreversible one.
   */
  tone?: 'positive' | 'caution' | 'negative';
  /**
   * Set only where a MISSING bucket means "this server has not got the field yet" rather
   * than "the figure is zero" — i.e. on the one optional member of the contract. Such a
   * cell prints {@link ABSENT_MARK} and drops its tone. The other five are `@required`, so
   * their `| undefined` is a codegen artifact and the 0 fallback below is the right answer.
   */
  absentIsUnknown?: true;
}

const CELLS: Cell[] = [
  // 窗口内新增 vs 窗口前存量 is what carries the momentum/base distinction now that the
  // labels are Chinese-only — the two are the split of 总在途 below, not of each other.
  { label: '后劲 · 窗口内新增', bucket: (m) => m?.momentum, tone: 'positive' },
  { label: '底盘 · 窗口前存量', bucket: (m) => m?.base },
  { label: '总在途', bucket: (m) => m?.open },
  { label: '上线(过去)', bucket: (m) => m?.launched },
  // Two things about this cell that the label cannot carry, both from the contract:
  //   · 30 days, not the 90 the mock prints — the threshold is a server constant, and
  //     the contract wins over the mock.
  //   · ⭐ THE ONLY CELL NOT SCOPED TO THE WINDOW. The staleness clock is anchored on
  //     TODAY ("ANCHORED ON TODAY, NOT ON THE WINDOW" in FlowMetrics#notFollowedUp), so
  //     asking about Q1 reports which of those opportunities is neglected now, not which
  //     looked neglected in March. It is also a SUBSET of 总在途 three cells left,
  //     so it can never exceed it — the two are not independent totals.
  { label: '30 天未更新', bucket: (m) => m?.notFollowedUp, tone: 'caution' },
  // Bad news like 30 天未更新, but a DIFFERENT kind, which is why this one is RED and that one
  // is amber. The contract is explicit about the difference: 未更新 is a subset of 总在途
  // (alive, nobody touched it — still rescuable), whereas 丢单 is closed and final. The two
  // never overlap, and neither belongs to Open.
  //   · Scoped to the window, on the same system_close_date as 上线(过去) — the two are the
  //     window's finished set split by outcome, so 上线 + 丢单 is "everything that ended".
  //   · ⭐ THE ONLY OPTIONAL BUCKET. It shipped after the other five (a required member would
  //     have broken every existing consumer's fixtures the moment the model reached their
  //     version set, and IVTHub and this portal sit in different version sets), so absence
  //     means "the deployed service predates the field" — NOT zero. Hence absentIsUnknown.
  { label: '丢单', bucket: (m) => m?.lost, tone: 'negative', absentIsUnknown: true },
];

interface Props {
  total: OverallTotals | undefined;
}

export function KpiBand({ total }: Props) {
  return (
    <div className="ivt-dashboard__kpi-band">
      {CELLS.map((cell) => {
        const bucket = cell.bucket(total?.metrics);
        // The tone is dropped when the figure is unknown: a red — reads as a bad number, and
        // there is no number. Grey says "nothing to report here" instead.
        const unknown = cell.absentIsUnknown === true && bucket === undefined;
        const tone = unknown ? ' is-absent' : cell.tone ? ` is-${cell.tone}` : '';
        return (
          <div className="ivt-dashboard__kpi-cell" key={cell.label}>
            <div className="ivt-dashboard__kpi-label">{cell.label}</div>
            {/* opps rides the number's baseline, as in the design. */}
            <div
              className={`ivt-dashboard__kpi-value${tone}`}
              // The dash alone tells a screen reader nothing — "em dash" or silence. The unit
              // is dropped with it, because "— opps" would read as a count of dashes.
              aria-label={unknown ? `${cell.label}：服务端暂未提供该指标` : undefined}
            >
              {unknown ? (
                ABSENT_MARK
              ) : (
                <>
                  {bucket?.oppCount ?? 0}
                  <span className="ivt-dashboard__kpi-unit">opps</span>
                </>
              )}
            </div>
            <div className={`ivt-dashboard__kpi-arr${tone}`}>
              {unknown ? ABSENT_MARK : formatArr(bucket?.arr)}
            </div>
          </div>
        );
      })}
      <VitalityPills vitality={total?.vitality} />
    </div>
  );
}
